import type { MomentSearchResponse } from '../services/momentSearch.service';

export interface MomentSearchQuickSearch {
  label: string;
  query: string;
}

const MAX_QUICK_SEARCHES = 4;

const cleanValue = (value: string | null | undefined): string => value?.trim() ?? '';
const normalize = (value: string): string => value.toLocaleLowerCase();

const pushUnique = (
  quickSearches: MomentSearchQuickSearch[],
  seen: Set<string>,
  label: string,
  query = label,
) => {
  const cleanLabel = cleanValue(label);
  const cleanQuery = cleanValue(query);
  const key = normalize(cleanQuery);

  if (!cleanLabel || !cleanQuery || seen.has(key) || quickSearches.length >= MAX_QUICK_SEARCHES) {
    return;
  }

  seen.add(key);
  quickSearches.push({ label: cleanLabel, query: cleanQuery });
};

const pushCategorySearch = (
  quickSearches: MomentSearchQuickSearch[],
  seen: Set<string>,
  category: string,
  suffix: string,
) => {
  const cleanCategory = cleanValue(category);
  if (!cleanCategory) {
    return;
  }

  pushUnique(quickSearches, seen, `${cleanCategory} ${suffix}`, cleanCategory);
};

const isPeopleCategory = (value: string): boolean => {
  const normalized = normalize(value);
  return normalized === 'people'
    || normalized.includes('people')
    || normalized.includes('creator')
    || normalized.includes('publisher')
    || normalized.includes('streamer');
};

const movePeopleUpcomingLast = (
  quickSearches: MomentSearchQuickSearch[],
  response: MomentSearchResponse,
): MomentSearchQuickSearch[] => {
  const peopleUpcoming = response.groups.upcoming.find((result) => isPeopleCategory(result.categoryLabel || result.category));
  if (!peopleUpcoming?.category) {
    return quickSearches;
  }
  const peopleLabel = peopleUpcoming.categoryLabel || peopleUpcoming.category;

  const peopleSearch: MomentSearchQuickSearch = {
    label: `${peopleLabel} upcoming`,
    query: peopleUpcoming.category,
  };
  const withoutPeopleUpcoming = quickSearches.filter((quickSearch) => normalize(quickSearch.label) !== normalize(peopleSearch.label));

  return [...withoutPeopleUpcoming.slice(0, MAX_QUICK_SEARCHES - 1), peopleSearch];
};

export const deriveMomentSearchQuickSearches = (
  response: MomentSearchResponse | null,
): MomentSearchQuickSearch[] => {
  if (!response) {
    return [];
  }

  const quickSearches: MomentSearchQuickSearch[] = [];
  const seen = new Set<string>();

  for (const result of response.groups.liveMoments) {
    pushCategorySearch(quickSearches, seen, result.categoryLabel || result.category, 'live moments');
  }

  for (const result of response.groups.live) {
    pushCategorySearch(quickSearches, seen, result.categoryLabel || result.category, 'live now');
  }

  for (const result of response.groups.upcoming) {
    pushCategorySearch(quickSearches, seen, result.categoryLabel || result.category, 'upcoming');
  }

  for (const result of response.groups.moments) {
    pushCategorySearch(quickSearches, seen, result.categoryLabel || result.category, 'moments');
  }

  for (const result of response.groups.publishers) {
    pushCategorySearch(quickSearches, seen, result.categoryLabel, 'sessions');
  }

  for (const result of response.groups.past) {
    pushCategorySearch(quickSearches, seen, result.categoryLabel || result.category, 'moments');
  }

  return movePeopleUpcomingLast(quickSearches, response);
};
